import React from 'react';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { Button, Input, Space, Checkbox, Select, message, Modal, Switch, Popover, Tooltip, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
// (tag drawer drag/drop UI moved into shared ProspectTagsDrawer)
import {
    deleteProspectTag,
    getProspectTagList,
    getProspectTagOptions,
    getProspectPage,
    reorderProspectTags,
    saveProspectSetting,
    updateProspectEhrByIds,
    updateProspectStatusByIds,
} from 'api/prospect';
import CommonTable from 'components/CommonTable';
import { PATH } from 'constants/path';
import AvaIcon from 'assets/common/ava.png';
import { useNavigate } from 'react-router-dom';
import ProviderStore from 'store/Provider';
import { EStatus, EProspectType, Prospect, EOnBoardingStatus } from 'types/common';
import { exportExcel } from 'utils/excel';
import { getTimeStr } from 'utils/calendar';
import dayjs from 'utils/dayjs';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import UpdateEmailModal from './UpdateEmailModal';
import Icons from './imgs';
import {
    getHasReadTermsForExport,
    setHasReadTermsForExport,
    getHasSetNotifyEmails,
    setHasSetNotifyEmails,
    setShowAvaSettingInProsepect,
    getShowAvaSettingInProsepect,
    getUserId,
} from 'utils/localstore';
import { isDevEnv, isLocalhost, isTestEnv, canShowGroupPracticeView } from 'utils/common';
import SliderPanel from 'components/SliderPanel';
import icon from './imgs/icon.svg';
import useProviderNotifyEmails from 'hooks/useProviderNotifyEmails';
import type { CheckboxOptionType } from 'antd/lib/checkbox';
import HomeStore from 'store/Home';
import useProviderServiceFlag from 'hooks/useProviderServiceFlag';
import Frame from './imgs/frame.png';
import AvaConfigHome from './AvaConfigHome';
import copy from 'copy-to-clipboard';
import { sendReminder } from 'api/inbox';
import {
    getGroupProspectList,
    TGroupProspectListReq,
    gpUpdateProspectStatus,
    gpGetProspectTagOptions,
    gpSendMessage,
} from 'api/group';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType, EAIChatPageId } from 'types/ai';
import AIChat from 'components/AIChat';
import { TProspectListPageReq } from 'types/prospect';
import { AddClientTagsModal, ClientTags, ProspectTagsDrawer } from 'components/ClientTags';
import type { ProspectTagVo } from 'types/prospectTag';
import { addProspectTagsIncremental, fetchProspectTags } from 'utils/prospectTags';
import useCurrentUser from 'hooks/useCurrentUser';
import { TGPSendMessageParams } from 'types/inbox';
import EligibilityStatusTag, { ELIGIBILITY_STATUS_CONFIG } from 'components/EligibilityStatusTag';
import { EEligibilityStatus, ELIGIBILITY_FILTER_PARAM_BY_TAG } from 'types/insuranceEligibility';
import { getClientEligibility } from 'utils/clientEligibility';

const allValue = 'All';

// Eligibility 表头筛选分组：按 biller 分诊逻辑（payer 答复可用 / 答案不可用 / 从未查过），不是字母序。
// NEEDS_RECHECK 不在筛选里，也不再出现在这一列——2026-09-17 拍板：这一列只报后端给的状态，
// 7 天 stale 不再前端派生成 tag；后端行状态可携带 SERVICE_NOT_COVERED/CONFLICTING_AMOUNTS/CHECK_FAILED，补进对应组
const ELIGIBILITY_FILTER_GROUPS: EEligibilityStatus[][] = [
    [
        EEligibilityStatus.ELIGIBLE,
        EEligibilityStatus.NO_COST_ESTIMATE,
        EEligibilityStatus.OUT_OF_NETWORK,
        EEligibilityStatus.INACTIVE,
        EEligibilityStatus.SERVICE_NOT_COVERED,
        EEligibilityStatus.CONFLICTING_AMOUNTS,
    ],
    [EEligibilityStatus.COULD_NOT_VERIFY, EEligibilityStatus.PAYER_NOT_MATCHED, EEligibilityStatus.CHECK_FAILED],
    [EEligibilityStatus.NEVER_CHECKED, EEligibilityStatus.NO_INSURANCE],
];

const Page = () => {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [show, setShow] = React.useState(false);

    const [currentUser] = useCurrentUser();
    const showGroupPracticeView = canShowGroupPracticeView(currentUser);
    const [activeTab, setActiveTab] = React.useState<'practice' | 'my'>('practice');

    const navigate = useNavigate();
    const [pendingNameFilter, setPendingNameFilter] = React.useState('');
    const [nameFilter, setNameFilter] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<string>(allValue);
    const [typeFilter, setTypeFilter] = React.useState<string>(allValue);
    const [contactFilter, setContactFilter] = React.useState<string>();
    const [showUpdateEmailModal, setShowUpdateEmailModal] = React.useState(false);

    const [ehrUpdating, setEhrUpdating] = React.useState(false);
    const [statusUpdating, setStatusUpdating] = React.useState(false);
    const [patientPortalFilter, setPatientPortalFilter] = React.useState<string>();
    const [eligibilityFilter, setEligibilityFilter] = React.useState<EEligibilityStatus[]>([]);
    // ELIGIBILITY LAST CHECKED 列的服务端排序（orderKey ELIGIBILITY_LAST_CHECKED_AT；未查过恒排最后）
    const [eligSortOrder, setEligSortOrder] = React.useState<'ASC' | 'DESC'>();
    const [pendingTagFilter, setPendingTagFilter] = React.useState<string[]>([]);
    const [tagFilter, setTagFilter] = React.useState<string[]>([]);
    const [pendingProviderFilter, setPendingProviderFilter] = React.useState('');
    const [providerFilter, setProviderFilter] = React.useState('');

    const pageReq: TProspectListPageReq = React.useMemo(() => {
        const req: TProspectListPageReq = {
            pageNo: page,
            pageSize,
            includeSample: false,
            orderKey: 'CREATE_AT',
            orderType: 'DESC',
        };

        if (typeFilter !== allValue) {
            req.type = typeFilter;
        }

        if (nameFilter) {
            const v = nameFilter.trim();
            // Heuristic: input like email → use exact email match; otherwise use name fuzzy match.
            if (v.includes('@')) {
                req.email = v;
            } else {
                req.name = v;
            }
        }

        if (patientPortalFilter) {
            if (patientPortalFilter === 'Not sent') {
                req.on_boarding_status = ['NOT_SENT'];
            } else if (patientPortalFilter === EOnBoardingStatus.IN_PROGRESS) {
                req.on_boarding_status = ['SENT', 'IN_PROGRESS'];
            } else if (patientPortalFilter === EOnBoardingStatus.COMPLETE) {
                req.on_boarding_status = ['COMPLETE'];
            }
        }

        if (tagFilter.length > 0) {
            // Prefer camelCase input; request layer also sends snake_case for compatibility.
            req.tagNames = tagFilter;
        }

        if (providerFilter) {
            req.providerName = providerFilter.trim();
        }

        if (statusFilter !== allValue) {
            req.status = statusFilter;
        }

        if (contactFilter) {
            req.contactType = contactFilter;
        }

        if (eligibilityFilter.length > 0) {
            // UI 词汇表 → 后端 eligibilityStatus 枚举集合（COULD_NOT_VERIFY 等一对多展开）
            req.eligibilityStatus = Array.from(
                new Set(eligibilityFilter.flatMap((tag) => ELIGIBILITY_FILTER_PARAM_BY_TAG[tag] || [])),
            );
        }

        if (eligSortOrder) {
            req.orderKey = 'ELIGIBILITY_LAST_CHECKED_AT';
            req.orderType = eligSortOrder;
        }

        return req;
    }, [
        page,
        pageSize,
        typeFilter,
        nameFilter,
        patientPortalFilter,
        tagFilter,
        providerFilter,
        statusFilter,
        eligibilityFilter,
        eligSortOrder,
        contactFilter,
    ]);

    const isPracticeView = showGroupPracticeView && activeTab === 'practice';

    const fetchProspects = React.useCallback(() => {
        if (isPracticeView) {
            const gpReq: TGroupProspectListReq = {
                pageNo: pageReq.pageNo,
                pageSize: pageReq.pageSize,
                name: pageReq.name,
                email: pageReq.email,
                phone: pageReq.phone,
                type: pageReq.type,
                on_boarding_status: pageReq.on_boarding_status,
                include_sample: pageReq.includeSample,
                tagNames: pageReq.tagNames,
                providerName: pageReq.providerName,
                orderKey: pageReq.orderKey,
                orderType: pageReq.orderType,
                status: pageReq.status,
                contactType: pageReq.contactType,
                eligibilityStatus: pageReq.eligibilityStatus,
            };
            return getGroupProspectList(gpReq);
        }
        return getProspectPage(pageReq);
    }, [isPracticeView, pageReq]);

    const { data, loading, run } = useRequest(fetchProspects, {
        refreshDeps: [fetchProspects],
    });
    const [selectKeys, setSelectKeys] = React.useState<number[]>([]);
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [checked, setChecked] = React.useState(false);

    const [showAvaModal, setShowAvaModal] = React.useState<boolean>(false);
    const [showAvaHome, setShowAvaHome] = React.useState(false);

    const [notifyEmails, , fetchNotifyEmails] = useProviderNotifyEmails();
    const hasNotifyEmails = notifyEmails && notifyEmails.length > 0;

    const [collapse, setCollapse] = React.useState(!getHasSetNotifyEmails());
    const [hasInitCollapseStatus, setHasInitCollapseStatus] = React.useState(false);

    const [isOpenAva, setIsOpenAva] = React.useState(false);
    const [showOpenAvaPannel] = React.useState(false);
    const [switching, setSwitching] = React.useState(false);

    const [getUser] = ProviderStore.useStore();
    const user = getUser('data');
    const [getHomeSore] = HomeStore.useStore();
    const homeInfo = getHomeSore('data');
    const showChat = homeInfo && !homeInfo.klarityUser;
    const uuid = user?.providerUid;
    const [lastMessageStatusFilter] = React.useState<string>();

    const [replyByFilter] = React.useState<'PROVIDER' | 'AI'>();

    const pagedData = data?.data?.data;
    const listData: Prospect[] = React.useMemo(() => pagedData?.list || [], [pagedData?.list]);
    const totalRecords: number = pagedData?.totalRecords || 0;

    const normalizeTag = React.useCallback((t: unknown): string => {
        if (t == null) return '';
        if (typeof t === 'string') return t.trim();
        if (typeof t === 'object') {
            const maybe = t as { tagName?: unknown; name?: unknown; label?: unknown };
            const candidate = maybe.tagName ?? maybe.name ?? maybe.label;
            if (typeof candidate === 'string') return candidate.trim();
        }
        // Avoid default object stringification like "[object Object]"
        return '';
    }, []);

    const normalizeTags = React.useCallback(
        (tags: unknown): string[] => {
            if (!Array.isArray(tags)) return [];
            return tags.map(normalizeTag).filter(Boolean);
        },
        [normalizeTag],
    );

    // Keep an "all clients" total that doesn't change with search filters.
    // Updated only when no server-side filters are applied.
    const [totalAllClients, setTotalAllClients] = React.useState<number>(0);

    let list: Prospect[] = listData;

    const [newMsgClientId, setNewMsgClientId] = React.useState<number>();
    const [newMsgIdentityId, setNewMsgIdentityId] = React.useState<number>();
    const [newMsgContent, setNewMsgContent] = React.useState<string>('');
    const [showNewMsgModal, setShowNewMsgModal] = React.useState<boolean>(false);
    const [sendingNewMsg, setSendingNewMsg] = React.useState<boolean>(false);
    const [resetFlag, setResetFlag] = React.useState<{}>({});
    const [sendingClientId, setSendingClientId] = React.useState<number>();

    const [tagModalOpen, setTagModalOpen] = React.useState(false);
    const [taggingClient, setTaggingClient] = React.useState<Prospect | null>(null);
    const [taggingExistingTags, setTaggingExistingTags] = React.useState<string[]>([]);
    const [loadingTaggingExistingTags, setLoadingTaggingExistingTags] = React.useState(false);
    const [tagListCache, setTagListCache] = React.useState<Record<number, ProspectTagVo[]>>({});

    const [tagDrawerOpen, setTagDrawerOpen] = React.useState(false);
    const [tagDrawerProspect, setTagDrawerProspect] = React.useState<Prospect | null>(null);
    const [tagDrawerTags, setTagDrawerTags] = React.useState<ProspectTagVo[]>([]);
    const [tagDrawerLoading] = React.useState(false);
    const [tagDrawerSaving, setTagDrawerSaving] = React.useState(false);

    const { data: tagOptionsResp, run: refreshTagOptions } = useRequest(
        () => (isPracticeView ? gpGetProspectTagOptions() : getProspectTagOptions()),
        { refreshDeps: [isPracticeView] },
    );
    const tagOptions: string[] = React.useMemo(() => {
        const raw = tagOptionsResp?.data?.data;
        return Array.isArray(raw) ? raw.filter(Boolean) : [];
    }, [tagOptionsResp?.data?.data]);

    const providerId = getUserId();

    const deleteTagById = React.useCallback(
        async (prospectId: number, tagId: number) => {
            const res = await deleteProspectTag(tagId);
            if (res.error) {
                message.error('Failed to remove tag.');
                return false;
            }
            setTagListCache((prev) => ({
                ...prev,
                [prospectId]: (prev[prospectId] || []).filter((x) => x.id !== tagId),
            }));
            setTagDrawerTags((prev) => prev.filter((x) => x.id !== tagId));
            // Options list is "distinct tags used by provider's prospects" and may change after deletion.
            refreshTagOptions();
            run();
            return true;
        },
        [refreshTagOptions, run],
    );

    const reorderDrawerTags = React.useCallback(
        async (prospectId: number, nextTagNames: string[]) => {
            if (tagDrawerSaving) return;
            const byName = new Map(tagDrawerTags.map((t) => [t.tagName, t]));
            const items = nextTagNames
                .map((name, idx) => {
                    const t = byName.get(name);
                    if (!t) return null;
                    return { id: t.id, sortOrder: idx };
                })
                .filter(Boolean) as Array<{ id: number; sortOrder: number }>;

            if (items.length === 0) return;
            setTagDrawerSaving(true);
            try {
                const res = await reorderProspectTags(prospectId, items);
                if (res.error) {
                    message.error('Failed to reorder tags.');
                    return;
                }
                const next = items
                    .map((it) => {
                        const t = tagDrawerTags.find((x) => x.id === it.id);
                        return t ? { ...t, sortOrder: it.sortOrder } : null;
                    })
                    .filter(Boolean) as ProspectTagVo[];
                setTagDrawerTags(next);
                setTagListCache((prev) => ({ ...prev, [prospectId]: next }));
            } finally {
                setTagDrawerSaving(false);
            }
        },
        [tagDrawerSaving, tagDrawerTags],
    );

    const tagDrawerName = React.useMemo(() => {
        if (!tagDrawerProspect) return '';
        return `${tagDrawerProspect.firstName || ''} ${tagDrawerProspect.lastName || ''}`.trim();
    }, [tagDrawerProspect]);

    const onClickDiscard = async () => {
        setNewMsgClientId(undefined);
        setNewMsgContent('');
        setResetFlag({});
    };

    const onClickSend = async () => {
        if (!newMsgClientId || !newMsgContent || !providerId) {
            message.error('Please select a client and enter a message.');
            return;
        }

        // if (!checkContent((newMsgContent))) {
        //     message.error('Invalid content! Please check the links in your message.');
        //     return;
        // }

        setSendingNewMsg(true);
        const msg = newMsgContent.replace(/<[^>]+>/g, '');
        const recipientType = isPracticeView ? 'GP_PROSPECT_IDENTITY' : 'PATIENT';
        const targetClient = listData?.find((item) => item.id === newMsgClientId);
        const _data: TGPSendMessageParams = {
            recipientType,
            targetId: newMsgClientId,
            message: msg,
        };
        if (targetClient && isPracticeView) {
            _data.targetId = targetClient.identityId;
        }
        const res = await gpSendMessage(_data);
        setSendingNewMsg(false);
        if (!res.error) {
            message.success('Sent successfully!');
            setShowNewMsgModal(false);
            setNewMsgClientId(undefined);
            setNewMsgContent('');
            setResetFlag({});
            run();
        }
    };

    const [flag] = useProviderServiceFlag();
    const isKlarity = !!flag?.klarityServiceFlag;
    // const isKlarityPro = currentPlan?.type === TSubscriptionType.ESSENTIAL;
    // const withoutInbox = homeInfo && homeInfo.freeUser && !homeInfo.ppaProvider;
    const withoutInbox = false;

    // const lastMessageStatusOptions: CheckboxOptionType[] = [
    //     {
    //         label: 'Read',
    //         value: 1,
    //     },
    //     {
    //         label: 'Unread',
    //         value: 0,
    //     },
    // ];

    const patientPortalOptions: CheckboxOptionType[] = React.useMemo(
        () => [
            {
                label: 'Signed up',
                value: EOnBoardingStatus.COMPLETE,
            },
            {
                label: 'In-progress',
                value: EOnBoardingStatus.IN_PROGRESS,
            },
            {
                label: 'Not sent',
                value: 'Not sent',
            },
        ],
        [],
    );

    const handleChangeAvaSwitch = async (check: boolean) => {
        const current = isOpenAva;
        setIsOpenAva(check);
        setSwitching(true);
        const result = await saveProspectSetting(null, check);
        if (!result.error) {
            message.success('Saved successfully');
        } else {
            //roll back
            setIsOpenAva(current);
        }
        setSwitching(false);
    };

    // React.useEffect(() => {
    //     if (info) {
    //         setIsOpenAva(info.avaAutoReply);
    //         setShowOpenAvaPannel(!info.avaAutoReply)
    //     }
    // }, [info])

    React.useEffect(() => {
        if (notifyEmails !== undefined) {
            //loader from server
            if (notifyEmails.length === 0 && !hasInitCollapseStatus && collapse === false) {
                setCollapse(true);
                setHasInitCollapseStatus(true);
            }
        }
    }, [notifyEmails, hasInitCollapseStatus, collapse]);

    React.useEffect(() => {
        //with inbox
        //https://gitkraken.dev/link/dnNjb2RlOi8vZWFtb2Rpby5naXRsZW5zL2xpbmsvci82YzQ2OTA5M2VmNWRkMmI0MTE1YmM2MDk3ZTdhMGY4ZjRhODZkYmQ5L2Yvc3JjL3BhZ2VzL0xheW91dC9pbmRleC50c3g%2FdXJsPWdpdCU0MGdpdGxhYi5jb20lM0FrbGFyaXR5LXNhYXMlMkZmcm9udCUyRmJveHByYWN0aWNlLmdpdCZsaW5lcz0xOTY%3D?origin=gitlens
        const autoShow = !getShowAvaSettingInProsepect() && withoutInbox;
        if (autoShow) {
            setShowAvaSettingInProsepect('true');
            setShowAvaHome(true);
        }
    }, [withoutInbox]);

    // Note: list is now primarily filtered server-side via `/prospect/page`.
    // Keep the following filters as "current-page only" presentation filters.
    if (lastMessageStatusFilter !== undefined) {
        list = list.filter((item: Prospect) => `${item.isRead}` === `${lastMessageStatusFilter}`);
    }
    if (replyByFilter) {
        list = list.filter((item: Prospect) => item.lastReplyBy?.toLowerCase() === replyByFilter.toLowerCase());
    }
    // Eligibility 筛选：solo/my 与 practice 视图都走服务端 eligibilityStatus 参数
    // （practice 为 identity-wide 语义），不再做当前页展示层过滤

    React.useEffect(() => {
        setPage(1);
    }, [
        nameFilter,
        typeFilter,
        patientPortalFilter,
        tagFilter,
        providerFilter,
        statusFilter,
        contactFilter,
        eligibilityFilter,
        eligSortOrder,
    ]);

    React.useEffect(() => {
        const hasServerSideFilter =
            !!nameFilter ||
            typeFilter !== allValue ||
            patientPortalFilter !== undefined ||
            tagFilter.length > 0 ||
            !!providerFilter;
        if (!hasServerSideFilter) {
            setTotalAllClients(totalRecords || 0);
        }
    }, [nameFilter, typeFilter, patientPortalFilter, tagFilter, providerFilter, totalRecords]);

    const displayTotalClients = totalAllClients || totalRecords || 0;

    // const rowSelection = {
    //     onChange: (selectedRowKeys: React.Key[]) => {
    //         setSelectKeys((selectedRowKeys as number[]) || []);
    //     },
    //     selectedRowKeys: selectKeys,
    // };

    const handleSendInvitation = React.useCallback(
        async (clientId: number) => {
            setSendingClientId(clientId);
            const result = await sendReminder(clientId, 'PROVIDER_PATIENT_INVITATION_WHEN_NOT_SENT');
            setSendingClientId(undefined);
            if (!result.error) {
                message.success('Invitation sent successfully');
                run();
            }
        },
        [run],
    );
    const handleSendReminder = React.useCallback(
        async (clientId: number) => {
            setSendingClientId(clientId);
            const result = await sendReminder(clientId, 'PROVIDER_PATIENT_ONBOARDING_IN_PROGRESS_REMINDER');
            setSendingClientId(undefined);
            if (!result.error) {
                message.success('Reminder sent successfully');
                run();
            }
        },
        [run],
    );

    const selectItems = list.filter((item: Prospect) => selectKeys.includes(item?.id as number));
    const hasSelect = selectItems.length > 0;
    let allActive = !!hasSelect;
    let allArchived = !!hasSelect;
    let allOnEhr = !!hasSelect;
    let allOffEhr = !!hasSelect;
    const showExport = !!hasSelect;
    selectItems?.forEach((item: Prospect) => {
        if (item.status === EStatus.Active) {
            allArchived = false;
        }
        if (item.status === EStatus.Archived) {
            allActive = false;
        }
        if (item.ehr === 'No') {
            allOnEhr = false;
        }
        if (item.ehr === 'Yes') {
            allOffEhr = false;
        }
    });

    const setEhr = async (val: 'Yes' | 'No') => {
        setEhrUpdating(true);
        try {
            const result = await updateProspectEhrByIds(selectKeys, val);
            if (result.error) {
                //message.error(result.error);
            } else {
                const msg = val === 'Yes' ? 'Mark as on EHR successfully' : 'Removed from EHR successfully';
                message.success(msg);
                setSelectKeys([]);
                run();
            }
        } finally {
            setEhrUpdating(false);
        }
    };

    const setStatus = async (val: EStatus) => {
        setStatusUpdating(true);
        try {
            const result = await updateProspectStatusByIds(selectKeys, val);
            if (result.error) {
                //message.error(result.error);
            } else {
                const msg = val === EStatus.Active ? 'Activate successfully' : 'Archived successfully';
                message.success(msg);
                setSelectKeys([]);
                run();
            }
        } finally {
            setStatusUpdating(false);
        }
    };

    const setStatusForSingle = React.useCallback(
        async (val: EStatus, item: Prospect) => {
            let result: any;
            if (isPracticeView && item.identityId) {
                result = await gpUpdateProspectStatus({ identityId: item.identityId, status: val }, item.providerId);
            } else {
                result = await updateProspectStatusByIds([item.id], val);
            }
            if (result.error) {
                //message.error(result.error);
            } else {
                const msg = val === EStatus.Active ? 'Activate successfully' : 'Archived successfully';
                message.success(msg);
                run();
            }
        },
        [run, isPracticeView],
    );

    const downloadExcel = () => {
        exportExcel({
            data: selectItems as unknown as Record<string, string | number | object>[],
            excelName: 'Client list',
        });
        setSelectKeys([]);
    };

    const columns: ColumnsType<Prospect> = React.useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                width: '100px',
                render: (text: string) => {
                    return <span>{text}</span>;
                },
            },
            {
                title: 'NAME',
                dataIndex: 'name',
                filterSearch: true,
                width: '200px',
                filterIcon: <SearchOutlined />,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            value={pendingNameFilter}
                            onChange={(e) => {
                                setPendingNameFilter(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && pendingNameFilter) {
                                    setNameFilter(pendingNameFilter);
                                }
                            }}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setNameFilter(pendingNameFilter);
                                }}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={() => {
                                    setPendingNameFilter('');
                                    setNameFilter('');
                                }}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                render: (name: string, item: Prospect) => {
                    //todo
                    const { isSample } = item;
                    return (
                        <div className={s.nameWrap}>
                            <div className={s.nameRight}>
                                <div className={`${s.name} ${item.status === EStatus.Archived ? s.nameArchived : ''}`}>
                                    {item.online === true ? (
                                        <span className={s.activate} />
                                    ) : (
                                        <span className={s.archive} />
                                    )}
                                    {/* {item.status === EStatus.Archived && (
                                    <span className={s.archive} />
                                )} */}
                                    <span
                                        className={s.nameText}
                                        onClick={() => {
                                            if (isPracticeView && item.identityId) {
                                                navigate(
                                                    `${PATH.DASHBOARD}/${PATH.GP_PROSPECTS_DETAIL}/${item.identityId}`,
                                                );
                                            } else {
                                                navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${item.id}`);
                                            }
                                        }}
                                    >
                                        {isSample ? '[Sample]' : ''} {item.firstName} {item.lastName}
                                    </span>
                                    {item.status === EStatus.Archived && (
                                        <span className={s.nameArchivedIcon}>Archived</span>
                                    )}
                                </div>
                                {/* <div className={s.itemWithIcon}>
                                <ClockCircleOutlined />
                                <a className={s.val} href={`mailto:${item.email}`}>{item.email}</a>
                            </div> */}
                            </div>
                        </div>
                    );
                },
            },
            // {
            //     title: 'CONTACT',
            //     dataIndex: 'email',
            //     filterSearch: true,
            //     width: '250px',
            //     filterIcon: <SearchOutlined />,
            //     filterDropdown: () => (
            //         <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            //             <Input
            //                 value={pendingNameFilter}
            //                 onChange={(e) => {
            //                     setPendingNameFilter(e.target.value);
            //                 }}
            //                 onKeyDown={(e) => {
            //                     if (e.key === 'Enter' && pendingNameFilter) {
            //                         setNameFilter(pendingNameFilter);
            //                     }
            //                 }}
            //                 style={{ marginBottom: 8, display: 'block' }}
            //             />
            //             <Space>
            //                 <Button
            //                     type="primary"
            //                     onClick={() => {
            //                         setNameFilter(pendingNameFilter);
            //                     }}
            //                     icon={<SearchOutlined />}
            //                     size="small"
            //                     style={{ width: 90 }}
            //                 >
            //                     Search
            //                 </Button>
            //                 <Button
            //                     onClick={() => {
            //                         setPendingNameFilter('');
            //                         setNameFilter('');
            //                     }}
            //                     size="small"
            //                     style={{ width: 90 }}
            //                 >
            //                     Reset
            //                 </Button>
            //             </Space>
            //         </div>
            //     ),
            //     render: (name: string, item: Prospect) => {
            //         return (
            //             <div className={s.nameWrap}>
            //                 <div className={s.nameRight}>
            //                     <div className={s.name}>{item.email}</div>
            //                     {!loading && item.tel && (
            //                         <div className={s.itemWithIcon}>
            //                             <PhoneOutlined />
            //                             {/* <a className={s.val} href={`tel:${item.tel}`}>{item.tel}</a> */}
            //                             <p className={s.val}>
            //                                 {displayItem(
            //                                     loading,
            //                                     'No phone number provided',
            //                                     item.tel,
            //                                 )}
            //                             </p>
            //                         </div>
            //                     )}
            //                 </div>
            //             </div>
            //         );
            //     },
            // },
            {
                title: 'CONTACT',
                dataIndex: 'email',
                filterSearch: true,
                width: '150px',
                filterDropdown: ({ close }) => {
                    return (
                        <div className={s.dropWrap}>
                            {[
                                { value: allValue, label: 'All' },
                                { value: 'email', label: 'Email' },
                                { value: 'tel', label: 'Telephone' },
                            ].map((item) => {
                                return (
                                    <div
                                        onClick={() => {
                                            if (item.value === allValue) {
                                                setContactFilter(undefined);
                                                close();
                                                return;
                                            }
                                            setContactFilter(item.value);
                                            close();
                                        }}
                                        className={s.dropItem}
                                    >
                                        {item.label}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
                render: (name: string, item: Prospect) => {
                    return (
                        <div className={s.contactWrap}>
                            {showChat && !item.isSample && (
                                <span
                                    onClick={() => {
                                        if (!item.lastReplyAt || isPracticeView) {
                                            setNewMsgIdentityId(item.identityId);
                                            setNewMsgClientId(item.id);
                                            setShowNewMsgModal(true);
                                            return;
                                        }
                                        let url = `${PATH.DASHBOARD}/${PATH.INBOX}?clientId=${item.id}`;
                                        if (isPracticeView) {
                                            url = `${PATH.DASHBOARD}/${PATH.INBOX}?clientId=${item.id}&tab=practice`;
                                        }
                                        navigate(url);
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="26"
                                        viewBox="0 0 25 26"
                                        fill="none"
                                    >
                                        <path
                                            d="M8.182 13.0012H8.19222M12.2729 13.0012H12.2831M16.3638 13.0012H16.374M21.4775 13.0012C21.4775 17.5198 17.3564 21.183 12.2729 21.183C10.6987 21.183 9.21671 20.8317 7.92086 20.2125L3.06836 21.183L4.49505 17.3785C3.59155 16.1126 3.06836 14.6112 3.06836 13.0012C3.06836 8.48246 7.18937 4.81934 12.2729 4.81934C17.3564 4.81934 21.4775 8.48246 21.4775 13.0012Z"
                                            stroke="#1890ff"
                                            strokeWidth="1.22727"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            )}
                            {item.email && (
                                <Popover open={false} content={<div>{item.email}</div>}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="26"
                                        viewBox="0 0 25 26"
                                        fill="none"
                                    >
                                        <path
                                            d="M3.06836 8.90803L11.1383 14.288C11.8254 14.746 12.7205 14.746 13.4075 14.288L21.4775 8.90803M5.11381 20.158H19.432C20.5617 20.158 21.4775 19.2422 21.4775 18.1126V7.8853C21.4775 6.75562 20.5617 5.83984 19.432 5.83984H5.11381C3.98414 5.83984 3.06836 6.75562 3.06836 7.8853V18.1126C3.06836 19.2422 3.98414 20.158 5.11381 20.158Z"
                                            stroke="#6B7280"
                                            strokeWidth="1.22727"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Popover>
                            )}
                            {item.tel && (
                                <Popover open={false} content={<div>{item.tel}</div>}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="26"
                                        height="26"
                                        viewBox="0 0 26 26"
                                        fill="none"
                                    >
                                        <path
                                            d="M3.61426 5.84038C3.61426 4.7107 4.53004 3.79492 5.65971 3.79492H9.01348C9.45369 3.79492 9.84452 4.07661 9.98373 4.49423L11.5156 9.0898C11.6765 9.57264 11.4579 10.1004 11.0027 10.328L8.69415 11.4822C9.82145 13.9825 11.8357 15.9968 14.336 17.1241L15.4903 14.8156C15.7179 14.3603 16.2456 14.1417 16.7285 14.3027L21.324 15.8345C21.7417 15.9738 22.0234 16.3646 22.0234 16.8048V20.1586C22.0234 21.2882 21.1076 22.204 19.9779 22.204H18.9552C10.4826 22.204 3.61426 15.3357 3.61426 6.8631V5.84038Z"
                                            stroke="#6B7280"
                                            strokeWidth="1.22727"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Popover>
                            )}
                        </div>
                    );
                },
            },
            ...(showGroupPracticeView && activeTab === 'practice'
                ? [
                      {
                          title: 'PROVIDER',
                          dataIndex: 'boundProviders',
                          width: '180px',
                          filterIcon: <SearchOutlined style={{ color: providerFilter ? '#1890ff' : undefined }} />,
                          filterDropdown: () => (
                              <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                                  <Input
                                      placeholder="Search by provider"
                                      value={pendingProviderFilter}
                                      onChange={(e) => {
                                          setPendingProviderFilter(e.target.value);
                                      }}
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                              setProviderFilter(pendingProviderFilter);
                                          }
                                      }}
                                      style={{ marginBottom: 8, display: 'block' }}
                                  />
                                  <Space>
                                      <Button
                                          type="primary"
                                          onClick={() => {
                                              setProviderFilter(pendingProviderFilter);
                                          }}
                                          icon={<SearchOutlined />}
                                          size="small"
                                          style={{ width: 90 }}
                                      >
                                          Search
                                      </Button>
                                      <Button
                                          onClick={() => {
                                              setPendingProviderFilter('');
                                              setProviderFilter('');
                                          }}
                                          size="small"
                                          style={{ width: 90 }}
                                      >
                                          Reset
                                      </Button>
                                  </Space>
                              </div>
                          ),
                          render: (
                              boundProviders: { firstName?: string; lastName?: string; credential?: string }[],
                          ) => {
                              if (!boundProviders || boundProviders.length === 0) return <span>-</span>;
                              const primary = boundProviders[0];
                              const primaryName = `${primary.firstName || ''} ${primary.lastName || ''}`.trim();
                              const extra = boundProviders.slice(1);
                              return (
                                  <div className={s.providerCell}>
                                      <span className={s.providerName}>{primaryName}</span>
                                      {extra.length > 0 && (
                                          <Tooltip
                                              title={extra
                                                  .map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim())
                                                  .join(', ')}
                                          >
                                              <span className={s.providerExtra}>+{extra.length}</span>
                                          </Tooltip>
                                      )}
                                  </div>
                              );
                          },
                      } as ColumnsType<Prospect>[number],
                  ]
                : []),
            {
                title: 'TAG',
                dataIndex: 'tags',
                width: '120px',
                filterIcon: <SearchOutlined />,
                filterDropdown: ({ close }) => {
                    return (
                        <div style={{ padding: 12, width: 280 }} onKeyDown={(e) => e.stopPropagation()}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Filter by tags</div>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select tags"
                                style={{ width: '100%' }}
                                value={pendingTagFilter}
                                onChange={(val) => {
                                    setPendingTagFilter((val || []).filter(Boolean) as string[]);
                                }}
                                options={tagOptions.map((t) => ({ value: t, label: t }))}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setPendingTagFilter([]);
                                        setTagFilter([]);
                                        close();
                                    }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => {
                                        setTagFilter(pendingTagFilter);
                                        close();
                                    }}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                    );
                },
                render: (_: unknown, item: Prospect) => {
                    const pid = item.id as number | undefined;
                    const cached = pid ? tagListCache[pid] : undefined;
                    const t = cached ? cached.map((x) => x.tagName) : normalizeTags(item.tags);
                    const openAddTagModal = () => {
                        setTaggingClient(item);
                        setTaggingExistingTags([]);
                        setTagModalOpen(true);

                        if (!item?.id) return;
                        setLoadingTaggingExistingTags(true);
                        getProspectTagList(item.id)
                            .then((res) => {
                                const next = (res?.data?.data || []).map((x) => x?.tagName).filter(Boolean) as string[];
                                setTaggingExistingTags(next);
                                setTagListCache((prev) => ({
                                    ...prev,
                                    [item.id as number]: ((res?.data?.data || []) as ProspectTagVo[])
                                        .slice()
                                        .sort((a, b) => a.sortOrder - b.sortOrder),
                                }));
                            })
                            .finally(() => {
                                setLoadingTaggingExistingTags(false);
                            });
                    };
                    return (
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                openAddTagModal();
                            }}
                        >
                            <ClientTags
                                tags={t}
                                maxVisible={4}
                                onMoreClick={() => {
                                    openAddTagModal();
                                }}
                                onTagAction={(tagName) => {
                                    if (!pid) return;
                                    const cachedTags = tagListCache[pid];
                                    const hit = cachedTags?.find((x) => x.tagName === tagName);
                                    if (hit?.id) {
                                        deleteTagById(pid, hit.id);
                                        return;
                                    }

                                    // Fallback: fetch tag list to resolve id, then delete.
                                    getProspectTagList(pid).then((res) => {
                                        const items = (res?.data?.data || []) as ProspectTagVo[];
                                        const found = items.find((x) => x.tagName === tagName);
                                        if (!found?.id) return;
                                        setTagListCache((prev) => ({
                                            ...prev,
                                            [pid]: items.slice().sort((a, b) => a.sortOrder - b.sortOrder),
                                        }));
                                        deleteTagById(pid, found.id);
                                    });
                                }}
                                tagActionText="Remove tag"
                                onAddClick={() => {
                                    openAddTagModal();
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                title: 'TYPE',
                dataIndex: 'type',
                width: '150px',
                filterDropdown: ({ close }) => {
                    return (
                        <div className={s.dropWrap}>
                            {[
                                { value: allValue, label: 'All types' },
                                { value: 'Prospect', label: 'Prospect' },
                                { value: 'Patient', label: 'Patient' },
                            ].map((item) => {
                                return (
                                    <div
                                        onClick={() => {
                                            setTypeFilter(item.value);
                                            close();
                                        }}
                                        className={s.dropItem}
                                    >
                                        {item.label}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
                render: (type) => {
                    return (
                        <>
                            {type === EProspectType.PATIENT ? (
                                <span className={`${s.statusP} ${s.statusPatient}`}>
                                    <span className={s.icon} />
                                    Patient
                                </span>
                            ) : (
                                <span className={s.statusP}>
                                    <span className={s.icon} />
                                    Prospect
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                title: 'PATIENT PORTAL',
                dataIndex: 'onBoardingStatus',
                width: '160px',
                filterDropdown: ({ close }) => {
                    return (
                        <div className={s.dropWrap}>
                            <div
                                onClick={() => {
                                    setPatientPortalFilter(undefined);
                                    close();
                                }}
                                className={s.dropItem}
                            >
                                All
                            </div>
                            {patientPortalOptions.map((item) => {
                                return (
                                    <div
                                        onClick={() => {
                                            setPatientPortalFilter(item.value as string);
                                            close();
                                        }}
                                        className={s.dropItem}
                                        key={item.value as string}
                                    >
                                        {item.label}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
                render: (status: EOnBoardingStatus) => {
                    let label: string = 'Not sent';
                    if (status === EOnBoardingStatus.COMPLETE) {
                        label = 'Signed up';
                    }
                    if (status === EOnBoardingStatus.IN_PROGRESS || status === EOnBoardingStatus.SENT) {
                        label = 'In-progress';
                    }
                    return <div>{label}</div>;
                },
            },
            {
                title: 'INSURANCE ELIGIBILITY',
                key: 'insuranceEligibility',
                width: '230px',
                filtered: eligibilityFilter.length > 0,
                filterDropdown: ({ close }) => {
                    const toggle = (status: EEligibilityStatus, isChecked: boolean) => {
                        setEligibilityFilter((prev) =>
                            isChecked ? [...prev, status] : prev.filter((v) => v !== status),
                        );
                    };
                    return (
                        <div className={s.eligFilterWrap}>
                            {ELIGIBILITY_FILTER_GROUPS.map((group, gi) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={gi}>
                                    {gi > 0 && <div className={s.eligFilterDivider} />}
                                    {group.map((status) => (
                                        <label key={status} className={s.eligFilterOpt}>
                                            <Checkbox
                                                checked={eligibilityFilter.includes(status)}
                                                onChange={(e) => toggle(status, e.target.checked)}
                                            />
                                            <span>{ELIGIBILITY_STATUS_CONFIG[status].label}</span>
                                        </label>
                                    ))}
                                </React.Fragment>
                            ))}
                            <div className={s.eligFilterDivider} />
                            <div className={s.eligFilterFoot}>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setEligibilityFilter([]);
                                        close();
                                    }}
                                >
                                    Reset
                                </Button>
                                <Button type="primary" size="small" onClick={() => close()}>
                                    OK
                                </Button>
                            </div>
                        </div>
                    );
                },
                render: (item: Prospect) => {
                    const elig = getClientEligibility(item);
                    const neverChecked = elig.status === EEligibilityStatus.NEVER_CHECKED;
                    const multiCard = neverChecked && (item.insurances?.length || 0) > 1;
                    return (
                        <div className={s.insElig}>
                            <EligibilityStatusTag status={elig.status} tipOverride={elig.tipOverride} />
                            {elig.carrier && (
                                <span className={s.insCarrier}>
                                    <span>{elig.carrier}</span>
                                    {multiCard && (
                                        <Tooltip
                                            title={
                                                <div>
                                                    {item.insurances!.map((ins) => (
                                                        <div key={ins.id}>{ins.carrier}</div>
                                                    ))}
                                                </div>
                                            }
                                        >
                                            <span style={{ marginLeft: 4, cursor: 'pointer' }}>...</span>
                                        </Tooltip>
                                    )}
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'ELIGIBILITY LAST CHECKED',
                key: 'eligibilityLastChecked',
                width: '180px',
                // 服务端排序（两个视图的接口都支持 ELIGIBILITY_LAST_CHECKED_AT；未查过恒排最后），
                // 见 CommonTable onChange
                sorter: true,
                // 显示真实日期而非相对时间，格式与 CREATE DATE 一致（相对时间放 detail 页）
                render: (item: Prospect) => {
                    const at = getClientEligibility(item).lastCheckedAt;
                    return <div>{at ? getTimeStr(dayjs(at), 'MM/DD/YYYY') : '—'}</div>;
                },
            },
            // {
            //     title: 'MESSAGE',
            //     dataIndex: 'isRead',
            //     width: '160px',
            //     filterDropdown: ({ close }) => {
            //         return (
            //             <div className={s.dropWrap}>
            //                 {lastMessageStatusOptions.map((item) => {
            //                     return (
            //                         <div
            //                             onClick={() => {
            //                                 setLastMessageStatusFilter(item.value as string);
            //                                 close();
            //                             }}
            //                             className={s.dropItem}
            //                             key={item.value as string}
            //                         >
            //                             {item.label}
            //                         </div>
            //                     );
            //                 })}
            //                 <div
            //                     onClick={() => {
            //                         setLastMessageStatusFilter(undefined);
            //                         close();
            //                     }}
            //                     className={s.dropItem}
            //                 >
            //                     All status
            //                 </div>
            //             </div>
            //         );
            //     },
            //     render: (status) => {
            //         return <div>{status ? 'Read' : (<span className={s.unread}>Unread</span>)}</div>;
            //     },
            // },
            // {
            //     title: 'CHANNEL',
            //     dataIndex: 'channel',
            //     width: '180px',
            //     //sorter: (a, b) => { return dayjs(a.createTime).valueOf() - dayjs(b.createTime).valueOf(); },
            //     render: (channel, item) => {
            //         return (
            //             <div>{`${item.channelId}` === '-1' ? 'Others' : channel}</div>
            //         );
            //     },
            // },
            {
                title: 'CREATE DATE',
                dataIndex: 'createTime',
                width: '160px',
                sorter: (a, b) => {
                    return dayjs(a.createTime).valueOf() - dayjs(b.createTime).valueOf();
                },
                render: (createTime) => {
                    return <div>{createTime ? getTimeStr(dayjs(createTime), 'MM/DD/YYYY') : 'Nil'}</div>;
                },
            },
            {
                title: 'LAST REPLIED',
                dataIndex: 'lastReplyAt',
                width: '160px',
                sorter: (a, b) => {
                    if (a.lastReplyAt && !b.lastReplyAt) {
                        return 1;
                    }
                    if (!a.lastReplyAt && b.lastReplyAt) {
                        return -1;
                    }
                    if (!a.lastReplyAt && !b.lastReplyAt) {
                        return 0;
                    }
                    return dayjs(a.lastReplyAt).valueOf() - dayjs(b.lastReplyAt).valueOf();
                },
                render: (lastReplyAt) => {
                    return <div>{lastReplyAt ? getTimeStr(dayjs(lastReplyAt), 'MM/DD/YYYY') : '-'}</div>;
                },
            },
            // {
            //     title: 'REPLIED BY',
            //     dataIndex: 'lastReplyBy',
            //     width: '160px',
            //     filterDropdown: ({ close }) => {
            //         return (
            //             <div className={s.dropWrap}>
            //                 <div
            //                     onClick={() => {
            //                         setReplyByFilter('PROVIDER');
            //                         close();
            //                     }}
            //                     className={s.dropItem}
            //                 >
            //                     You
            //                 </div>
            //                 <div
            //                     onClick={() => {
            //                         setReplyByFilter('AI');
            //                         close();
            //                     }}
            //                     className={s.dropItem}
            //                 >
            //                     Ava
            //                 </div>
            //                 <div
            //                     onClick={() => {
            //                         setReplyByFilter(undefined);
            //                         close();
            //                     }}
            //                     className={s.dropItem}
            //                 >
            //                     All
            //                 </div>
            //             </div>
            //         );
            //     },
            //     render: (type: string) => {
            //         let label = '';
            //         if (type?.toLowerCase() === 'provider') {
            //             label = 'You';
            //         }
            //         if (type?.toLowerCase() === 'ai') {
            //             label = 'Ava';
            //         }
            //         return <div>{label}</div>;
            //     },
            // },
            // {
            //     title: 'EHR',
            //     dataIndex: 'ehr',
            //     width: '100px',
            //     render: (ehr) => {
            //         return <div className={s.tag}>{ehr}</div>;
            //     },
            // },
            {
                title: '',
                width: activeTab === 'practice' ? '120px' : '230px',
                fixed: 'right',
                render: (item) => {
                    const isActive = item.status === EStatus.Active;
                    const isInPracticeView = activeTab === 'practice';
                    const showSendInvitation =
                        item.onBoardingStatus === EOnBoardingStatus.NOT_SENT && !isInPracticeView;
                    const showSendReminder =
                        item.onBoardingStatus === EOnBoardingStatus.IN_PROGRESS && !isInPracticeView;
                    const isDisabledFor24h = item.latestReminderTime
                        ? dayjs().diff(dayjs(item.latestReminderTime), 'hour') < 24
                        : false;
                    return (
                        <div className={s.acWrap}>
                            {showSendInvitation && (
                                <div
                                    className={`${s.sendLink} ${isDisabledFor24h ? s.sendLinkDisabled : ''}`}
                                    onClick={() => {
                                        if (sendingClientId || isDisabledFor24h) {
                                            return;
                                        }
                                        handleSendInvitation(item.id);
                                    }}
                                >
                                    {sendingClientId === item.id && <Spin className={s.spin} size="small" />}
                                    Send invite
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{
                                            width: '330px',
                                            padding: '16px',
                                            borderRadius: '24px',
                                        }}
                                        color="#ffffff"
                                        title={
                                            <div className={s.tooltipContent} style={{ color: '#333' }}>
                                                <div className={s.tooltipTitle}>📩 Invitation message preview</div>
                                                <div className={s.tooltipMessage}>
                                                    <p>Hi [Patient&apos;s Name],</p>
                                                    <p>
                                                        You’ve been invited to join provider [Provider Name]’s secure
                                                        patient portal.
                                                    </p>
                                                    <p>Through the portal, you can:</p>
                                                    <ul>
                                                        <li>Easily schedule and manage appointments</li>
                                                        <li>Contact your provider anytime</li>
                                                    </ul>
                                                    <p>
                                                        Signing up is quick and simple. Click the button below to get
                                                        started:
                                                    </p>
                                                    <p>[Signup Link]</p>
                                                    <p>
                                                        If you have any questions or need help, feel free to contact me.
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        placement="leftTop"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                                                stroke="#0E62F4"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Tooltip>
                                </div>
                            )}
                            {showSendReminder && (
                                <div
                                    className={`${s.sendLink} ${isDisabledFor24h ? s.sendLinkDisabled : ''}`}
                                    onClick={() => {
                                        if (sendingClientId || isDisabledFor24h) {
                                            return;
                                        }
                                        handleSendReminder(item.id);
                                    }}
                                >
                                    {sendingClientId === item.id && <Spin className={s.spin} size="small" />}
                                    Send reminder
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{
                                            width: '330px',
                                            padding: '16px',
                                            borderRadius: '24px',
                                        }}
                                        color="#ffffff"
                                        title={
                                            <div className={s.tooltipContent} style={{ color: '#333' }}>
                                                <div className={s.tooltipTitle}>📩 Reminder message preview</div>
                                                <div className={s.tooltipMessage}>
                                                    <p>Hi [Patient&apos;s Name],</p>
                                                    <p>
                                                        Just a quick reminder to complete your Klarity account setup.
                                                        This will allow you to message me directly when needed.
                                                    </p>
                                                    <p>
                                                        If you have any questions or need help, feel free to contact me.
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        placement="leftTop"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                                                stroke="#0E62F4"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Tooltip>
                                </div>
                            )}
                            {!showSendInvitation && !showSendReminder && !isInPracticeView && (
                                <div className={s.sendLink}>-</div>
                            )}
                            <Button
                                className={s.link}
                                onClick={() => {
                                    if (isPracticeView && item.identityId) {
                                        navigate(`${PATH.DASHBOARD}/${PATH.GP_PROSPECTS_DETAIL}/${item.identityId}`);
                                    } else {
                                        navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${item.id}`);
                                    }
                                }}
                                type="text"
                            >
                                View
                            </Button>
                            <Popover
                                trigger="click"
                                content={
                                    isActive ? (
                                        <div
                                            onClick={() => {
                                                setStatusForSingle(EStatus.Archived, item);
                                            }}
                                            className={s.popover}
                                        >
                                            Archive client
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => {
                                                setStatusForSingle(EStatus.Active, item);
                                            }}
                                            className={s.popover}
                                        >
                                            Activate client
                                        </div>
                                    )
                                }
                            >
                                <div className={s.moreAction}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="6"
                                        height="20"
                                        viewBox="0 0 6 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M3.00039 5.20039C1.67491 5.20039 0.60039 4.12587 0.60039 2.80039C0.60039 1.47491 1.67491 0.40039 3.00039 0.400391C4.32587 0.400391 5.40039 1.47491 5.40039 2.80039C5.40039 4.12587 4.32587 5.20039 3.00039 5.20039Z"
                                            fill="#6B7280"
                                        />
                                        <path
                                            d="M3.00039 12.4004C1.67491 12.4004 0.60039 11.3259 0.60039 10.0004C0.60039 8.67491 1.67491 7.60039 3.00039 7.60039C4.32587 7.60039 5.40039 8.67491 5.40039 10.0004C5.40039 11.3259 4.32587 12.4004 3.00039 12.4004Z"
                                            fill="#6B7280"
                                        />
                                        <path
                                            d="M0.60039 17.2004C0.60039 18.5259 1.67491 19.6004 3.00039 19.6004C4.32587 19.6004 5.40039 18.5259 5.40039 17.2004C5.40039 15.8749 4.32587 14.8004 3.00039 14.8004C1.67491 14.8004 0.60039 15.8749 0.60039 17.2004Z"
                                            fill="#6B7280"
                                        />
                                    </svg>
                                </div>
                            </Popover>
                        </div>
                    );
                },
            },
        ],
        [
            showGroupPracticeView,
            activeTab,
            pendingNameFilter,
            pendingProviderFilter,
            providerFilter,
            showChat,
            navigate,
            pendingTagFilter,
            tagOptions,
            tagListCache,
            normalizeTags,
            deleteTagById,
            patientPortalOptions,
            sendingClientId,
            handleSendInvitation,
            handleSendReminder,
            isPracticeView,
            setStatusForSingle,
            eligibilityFilter,
        ],
    );

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className={s.left}>
                    <h1>Clients</h1>
                </div>
                <div className={s.right}>
                    {withoutInbox && (
                        <Button
                            onClick={() => {
                                navigate(`${PATH.DASHBOARD}/${PATH.AVA_SETTING}`);
                            }}
                            style={{
                                marginRight: '16px',
                            }}
                        >
                            Settings
                        </Button>
                    )}
                    {(activeTab !== 'practice' || !isPracticeView) && (
                        <Button
                            onClick={() => {
                                setShow(true);
                            }}
                            style={{
                                marginRight: '16px',
                            }}
                        >
                            Create a new client
                        </Button>
                    )}

                    {flag && !isKlarity && (activeTab === 'my' || !isPracticeView) && (
                        <Button
                            type="primary"
                            disabled={!uuid}
                            onClick={() => {
                                let link = `https://www.helloklarity.com/my/signup?uid=${uuid}`;
                                if (isLocalhost()) {
                                    link = `http://localhost:3010/my/signup?uid=${uuid}`;
                                } else if (isDevEnv()) {
                                    link = `https://dev.d2ysjydukc4efk.amplifyapp.com/my/signup?uid=${uuid}`;
                                } else if (isTestEnv()) {
                                    link = `https://test.d2ysjydukc4efk.amplifyapp.com/my/signup?uid=${uuid}`;
                                }
                                copy(link || '', { format: 'text/plain' });
                                message.success('Copied to the clipboard');
                            }}
                        >
                            Copy client invitation link
                        </Button>
                    )}
                </div>
            </div>
            {showGroupPracticeView && (
                <div className={s.viewTabs}>
                    <button
                        type="button"
                        className={`${s.viewTab} ${activeTab === 'practice' ? s.viewTabActive : ''}`}
                        onClick={() => {
                            setActiveTab('practice');
                            setPage(1);
                        }}
                    >
                        Practice view
                    </button>
                    <button
                        type="button"
                        className={`${s.viewTab} ${activeTab === 'my' ? s.viewTabActive : ''}`}
                        onClick={() => {
                            setActiveTab('my');
                            setPage(1);
                        }}
                    >
                        My clients
                    </button>
                </div>
            )}
            {/* {
                isKlarity && showKlarityNote &&
                <div className={s.klarityNoteWrap}>
                    <div className={s.left}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M16.5 8C16.5 12.4183 12.9183 16 8.5 16C4.08172 16 0.5 12.4183 0.5 8C0.5 3.58172 4.08172 0 8.5 0C12.9183 0 16.5 3.58172 16.5 8ZM9.5 4C9.5 4.55228 9.05228 5 8.5 5C7.94772 5 7.5 4.55228 7.5 4C7.5 3.44772 7.94772 3 8.5 3C9.05228 3 9.5 3.44772 9.5 4ZM7.5 7C6.94772 7 6.5 7.44772 6.5 8C6.5 8.55229 6.94772 9 7.5 9V12C7.5 12.5523 7.94772 13 8.5 13H9.5C10.0523 13 10.5 12.5523 10.5 12C10.5 11.4477 10.0523 11 9.5 11V8C9.5 7.44772 9.05228 7 8.5 7H7.5Z" fill="#60A5FA" />
                        </svg>
                        <div className={s.info}>
                            {
                                isKlarityPro ?
                                    'All of your clients are already handled by Kiwi\'s virtual front desk team. You have 150 minutes call-time in your plan. To get more minutes, please contact Klarity support team.'
                                    :
                                    'All of your clients will be handled by Kiwi\'s virtual front desk team. You have 150 minutes call-time in total in your plan. To get more minutes, please explore other plans.'
                            }
                        </div>
                    </div>
                    <div className={s.right}>
                        <CloseOutlined className={s.closeIcon} onClick={() => setShowKlarityNote(false)} />
                        {
                            !isKlarityPro &&
                            <span
                                onClick={showKlarityPlanPannel}
                                className={s.rightBtn}
                            >Explore Kiwi plans
                            </span>
                        }
                        {
                            isKlarityPro &&
                            <span onClick={() => {
                                window.open(`mailto:${Email.support}`);
                            }}
                                className={s.rightBtn}
                            >Contact us
                            </span>
                        }
                    </div>
                </div>
            } */}
            <div className={`${s.avaPannel} ${showOpenAvaPannel ? s.avaPannelShow : ''}`}>
                <div className={s.avaTitle}>
                    <div className={s.avaTitleLeft}>
                        Turn on
                        <img src={AvaIcon} className={s.avaIcon} alt="ava" />
                        auto-reply and meet your new AI assistant 👋
                    </div>
                    <div className={s.avaTitleRight}>
                        <Switch loading={switching} checked={isOpenAva} onChange={handleChangeAvaSwitch} />
                    </div>
                </div>
                <div className={s.avaContent}>
                    Ava Auto-reply instantly responds to clients, while Co-pilot mode drafts replies for your approval.
                    <br />
                    Check more customization in Setting.
                </div>
            </div>
            <div style={{ display: 'none' }}>
                <SliderPanel
                    showVal={collapse}
                    title={
                        <div className={s.notificationSettingInfo}>
                            Prospect email notification preferences <img src={icon} />
                        </div>
                    }
                    contentCls={s.notificationSetting}
                    defaultHide
                    onShowStatusChange={(showInfo: boolean) => {
                        if (!showInfo) {
                            setHasSetNotifyEmails('true');
                        }
                    }}
                >
                    <>
                        <div className={s.subTitle}>
                            {hasNotifyEmails
                                ? `Your prospect notifications will be send to the following email addresses: ${notifyEmails.join(
                                      ', ',
                                  )}`
                                : "Stay informed and never miss a new client's important message! Add up to 5 email addresses to receive prospect notifications."}
                        </div>
                        <Button
                            onClick={() => {
                                setShowUpdateEmailModal(true);
                            }}
                            type="primary"
                            size="large"
                            className={s.btn}
                        >
                            {hasNotifyEmails ? 'Edit emails' : 'Add emails'}
                        </Button>
                    </>
                </SliderPanel>
            </div>
            <div className={s.filter}>
                <div className={s.left}>
                    {/* <p className={s.activeStatus}>Active clients</p>
                    <p className={s.archivedStatus}>Archived clients</p> */}
                    <h3>{displayTotalClients} clients</h3>
                </div>
                <div className={s.right}>
                    <Select
                        value={statusFilter}
                        style={{ width: 160, marginRight: 16 }}
                        onChange={(val) => {
                            setStatusFilter(val);
                        }}
                        options={[
                            { value: allValue, label: 'All status' },
                            { value: 'Active', label: 'Active' },
                            { value: 'Archived', label: 'Archived' },
                        ]}
                    />
                    {/* <Select
                        value={typeFilter}
                        style={{ width: 160, marginRight: 16 }}
                        onChange={(val) => {
                            setTypeFilter(val);
                        }}
                        options={[
                            { value: allValue, label: 'All types' },
                            { value: 'Prospect', label: 'Prospect' },
                            { value: 'Patient', label: 'Patient' },
                        ]}
                    /> */}
                </div>
            </div>
            <div className={s.control} style={{ display: 'none' }}>
                <div className={s.left}>
                    {selectKeys?.length > 0 ? (
                        <span className={s.mr8}>{`Selected ${selectKeys?.length} clients`}</span>
                    ) : (
                        <div className={s.item}>Total {list.length || 0} clients</div>
                    )}
                    {allOffEhr && (
                        <Button
                            loading={ehrUpdating}
                            className={s.action}
                            onClick={() => {
                                setEhr('Yes');
                            }}
                        >
                            {Icons.EhrOnIcon}
                            <span className={s.label}>Mark as on EHR</span>
                        </Button>
                    )}
                    {allOnEhr && (
                        <Button
                            loading={ehrUpdating}
                            className={s.action}
                            onClick={() => {
                                setEhr('No');
                            }}
                        >
                            {Icons.EhrOffIcon}
                            <span className={s.label}>Removed from EHR</span>
                        </Button>
                    )}

                    {allArchived && (
                        <Button
                            loading={statusUpdating}
                            className={s.action}
                            onClick={() => {
                                setStatus(EStatus.Active);
                            }}
                        >
                            {Icons.CheckIcon}
                            <span className={s.label}>Activate</span>
                        </Button>
                    )}

                    {allActive && (
                        <Button
                            loading={statusUpdating}
                            className={s.action}
                            onClick={() => {
                                setStatus(EStatus.Archived);
                            }}
                        >
                            {Icons.UnCheckIcon}
                            <span className={s.label}>Archive</span>
                        </Button>
                    )}
                    {showExport && (
                        <div
                            onClick={() => {
                                if (getHasReadTermsForExport()) {
                                    downloadExcel();
                                } else {
                                    setShowConfirm(true);
                                }
                            }}
                            className={s.action}
                        >
                            {Icons.DownloadIcon}
                            <span className={s.label}>Export</span>
                        </div>
                    )}
                    <span />
                </div>
            </div>
            <div className={s.tableWrap}>
                <CommonTable
                    bordered
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    data={list || []}
                    scroll={{ x: 'max-content' }}
                    onChange={(_pagination, _filters, sorterArg) => {
                        // 只有 ELIGIBILITY LAST CHECKED 列是服务端排序；其余列仍是本地 compare
                        const sv = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
                        if (sv?.columnKey === 'eligibilityLastChecked') {
                            let next: 'ASC' | 'DESC' | undefined;
                            if (sv.order === 'ascend') next = 'ASC';
                            else if (sv.order === 'descend') next = 'DESC';
                            setEligSortOrder(next);
                        } else if (sv?.columnKey && sv.order) {
                            // 切到本地排序列时清掉服务端排序，避免两种排序叠加
                            setEligSortOrder(undefined);
                        }
                    }}
                    pagination={{
                        current: page,
                        pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        onChange: (nextPage: number, nextSize: number) => {
                            setPage(nextPage);
                            if (nextSize !== pageSize) {
                                setPageSize(nextSize);
                            }
                        },
                    }}
                    // rowSelection={rowSelection}
                />
            </div>
            {show && (
                <AddAndUpdateProspects
                    show={show}
                    onSuccess={() => {
                        run();
                        setShow(false);
                    }}
                    onCannel={() => {
                        setShow(false);
                    }}
                />
            )}
            <UpdateEmailModal
                onCannel={() => {
                    setShowUpdateEmailModal(false);
                }}
                item={notifyEmails}
                onSuccess={() => {
                    setShowUpdateEmailModal(false);
                    fetchNotifyEmails();
                    setCollapse(false);
                    setHasSetNotifyEmails('true');
                }}
                show={showUpdateEmailModal}
            />
            <AddClientTagsModal
                open={tagModalOpen}
                clientName={`${taggingClient?.firstName || ''} ${taggingClient?.lastName || ''}`.trim() || 'Client'}
                existingTags={taggingExistingTags}
                onCancel={() => {
                    setTagModalOpen(false);
                    setTaggingClient(null);
                    setTaggingExistingTags([]);
                }}
                onSubmit={async (next) => {
                    if (!taggingClient?.id) return;
                    if (loadingTaggingExistingTags) return;
                    const { added } = await addProspectTagsIncremental({
                        prospectId: taggingClient.id,
                        existingTagNames: taggingExistingTags,
                        nextTagNames: normalizeTags(next),
                    });
                    if (added.length === 0) {
                        message.info('No new tags to add.');
                        setTagModalOpen(false);
                        setTaggingClient(null);
                        setTaggingExistingTags([]);
                        return;
                    }
                    message.success('Tags updated.');
                    // Refresh tag options so newly created tags appear in the filter dropdown immediately.
                    refreshTagOptions();
                    // Refresh tags cache to include ids for newly added tags.
                    const items = await fetchProspectTags(taggingClient.id);
                    setTagListCache((prev) => ({ ...prev, [taggingClient.id as number]: items }));
                    // If the drawer is open for the same client, update it immediately as well.
                    if ((tagDrawerProspect?.id as number | undefined) === (taggingClient.id as number)) {
                        setTagDrawerTags(items);
                    }
                    setTagModalOpen(false);
                    setTaggingClient(null);
                    setTaggingExistingTags([]);
                    run();
                }}
            />
            <ProspectTagsDrawer
                open={tagDrawerOpen}
                title={`Tags for ${tagDrawerName || 'client'}`}
                loading={tagDrawerLoading}
                tags={tagDrawerTags}
                onClose={() => {
                    setTagDrawerOpen(false);
                    setTagDrawerProspect(null);
                    setTagDrawerTags([]);
                }}
                onAddNewTags={() => {
                    if (!tagDrawerProspect) return;
                    setTaggingClient(tagDrawerProspect);
                    setTaggingExistingTags(tagDrawerTags.map((t) => t.tagName));
                    setTagModalOpen(true);
                }}
                onRemoveTag={(tagName) => {
                    const pid = tagDrawerProspect?.id as number | undefined;
                    if (!pid) return;
                    const hit = tagDrawerTags.find((x) => x.tagName === tagName);
                    if (!hit?.id) return;
                    deleteTagById(pid, hit.id);
                }}
                onReorderTags={(nextTagNames) => {
                    const pid = tagDrawerProspect?.id as number | undefined;
                    if (!pid) return;
                    reorderDrawerTags(pid, nextTagNames);
                }}
            />
            <Modal
                title="Terms for export"
                open={showConfirm}
                onOk={() => {
                    downloadExcel();
                    setHasReadTermsForExport('true');
                    setShowConfirm(false);
                }}
                onCancel={() => {
                    setShowConfirm(false);
                }}
                okText="Export the CSV file"
                okButtonProps={{
                    disabled: !checked,
                }}
            >
                <div>
                    <div className={s.modalP}>
                        We want to remind you that patient information contained within the .csv or .xlsx file you are
                        about to download is private and must be treated with utmost care. By downloading the file, you
                        agree to take full responsibility for safeguarding this information, and to comply with all
                        applicable laws and regulations.
                    </div>
                    <div className={s.modalP}>
                        <Checkbox
                            onChange={(e) => {
                                setChecked(e.target.checked);
                            }}
                        >
                            I have read and understand the terms.
                        </Checkbox>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showAvaModal}
                footer={null}
                width="960px"
                onCancel={() => {
                    setShowAvaModal(false);
                }}
            >
                <div className={s.avaWrap}>
                    <div className={s.left}>
                        <img src={Frame} />
                    </div>
                    <div className={s.right}>
                        <div className={s.header}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                            >
                                <path
                                    d="M19.9996 39.13C30.565 39.13 39.13 30.565 39.13 19.9996C39.13 9.43415 30.565 0.869141 19.9996 0.869141C9.43415 0.869141 0.869141 9.43415 0.869141 19.9996C0.869141 30.565 9.43415 39.13 19.9996 39.13Z"
                                    fill="#F0FCF6"
                                />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0 20C0 8.95432 8.95432 0 20 0C31.0457 0 40 8.95432 40 20C40 31.0457 31.0457 40 20 40C8.95432 40 0 31.0457 0 20ZM20 1.73913C9.91482 1.73913 1.73913 9.91482 1.73913 20C1.73913 30.0852 9.91482 38.2609 20 38.2609C30.0852 38.2609 38.2609 30.0852 38.2609 20C38.2609 9.91482 30.0852 1.73913 20 1.73913Z"
                                    fill="#00816B"
                                />
                                <path
                                    d="M30.9316 24.0999C32.8882 24.0999 34.12 22.7668 34.12 20.8103C34.12 18.8537 32.8882 17.2676 30.9316 17.2676V20.9115V24.0999Z"
                                    fill="#A3CDC3"
                                />
                                <path
                                    d="M9.52344 17.2673C7.56687 17.2673 6.33511 18.6003 6.33511 20.5569C6.33511 22.5135 7.56687 24.0996 9.52344 24.0996V20.4557V17.2673Z"
                                    fill="#A3CDC3"
                                />
                                <path
                                    d="M8.9668 15.1323C8.9668 13.8517 10.005 12.8135 11.2856 12.8135H29.4223C30.7029 12.8135 31.7411 13.8517 31.7411 15.1323V26.8922C31.7411 28.1728 30.7029 29.211 29.4223 29.211H11.2856C10.005 29.211 8.9668 28.1728 8.9668 26.8922V15.1323Z"
                                    fill="#00816B"
                                />
                                <path d="M19.0898 9.97949H21.8228V13.6234H19.0898V9.97949Z" fill="#00816B" />
                                <path
                                    d="M10.8906 16.853C10.8906 15.5724 11.9288 14.5342 13.2095 14.5342H27.7022C28.9829 14.5342 30.0211 15.5724 30.0211 16.853V24.969C30.0211 26.2496 28.9829 27.2878 27.7022 27.2878H13.2095C11.9288 27.2878 10.8906 26.2496 10.8906 24.969V16.853Z"
                                    fill="white"
                                />
                                <path
                                    d="M24.1499 22.1256C24.5413 22.1256 24.8585 21.6498 24.8585 21.0628C24.8585 20.4758 24.5413 20 24.1499 20C23.7586 20 23.4414 20.4758 23.4414 21.0628C23.4414 21.6498 23.7586 22.1256 24.1499 22.1256Z"
                                    fill="#00816B"
                                />
                                <path
                                    d="M18.4824 23.1885C19.1119 23.6082 19.8515 23.8321 20.608 23.8321C21.3646 23.8321 22.1042 23.6082 22.7336 23.1885"
                                    fill="#00816B"
                                />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M18.12 22.9475C18.2532 22.7478 18.5231 22.6938 18.7229 22.827C19.2809 23.199 19.9366 23.3976 20.6073 23.3976C21.278 23.3976 21.9337 23.199 22.4917 22.827C22.6915 22.6938 22.9615 22.7478 23.0947 22.9475C23.2279 23.1473 23.1739 23.4173 22.9741 23.5505C22.2732 24.0178 21.4497 24.2671 20.6073 24.2671C19.7649 24.2671 18.9414 24.0178 18.2405 23.5505C18.0407 23.4173 17.9867 23.1473 18.12 22.9475Z"
                                    fill="#00816B"
                                />
                                <path
                                    d="M17.064 22.1256C17.4553 22.1256 17.7725 21.6498 17.7725 21.0628C17.7725 20.4758 17.4553 20 17.064 20C16.6727 20 16.3555 20.4758 16.3555 21.0628C16.3555 21.6498 16.6727 22.1256 17.064 22.1256Z"
                                    fill="#00816B"
                                />
                                <path
                                    d="M23.6444 9.06804C23.6444 10.0743 22.2169 10.89 20.456 10.89C18.6951 10.89 17.2676 10.0743 17.2676 9.06804C17.2676 8.06181 18.6951 7.24609 20.456 7.24609C22.2169 7.24609 23.6444 8.06181 23.6444 9.06804Z"
                                    fill="#A3CDC3"
                                />
                            </svg>
                            <div className={s.hRight}>
                                <div className={s.title}>Ava auto-reply</div>
                                <div className={s.des}>Created by Klarity Health</div>
                            </div>
                        </div>
                        <span className={s.label}>New feature</span>
                        <div className={s.avaTitle}>
                            Meet <img className={s.ava} src={AvaIcon} />, your AI prospect assistant
                        </div>
                        <div className={s.avaDes}>
                            Ava helps you respond to clients instantly, boosting your response rate and saving you time.
                            Customize her tone, add your booking link, then you&apos;re all set. Let Ava handle the
                            initial communication while you focus on what you do best – providing excellent care.
                        </div>
                        <div className={s.btnWrap}>
                            <Button
                                onClick={() => {
                                    setShowAvaHome(true);
                                }}
                                block
                                size="large"
                                className={s.btn}
                            >
                                Learn more
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowAvaSettingInProsepect('true');
                                    navigate(`${PATH.DASHBOARD}/${PATH.AVA_SETTING}`);
                                }}
                                block
                                size="large"
                                type="primary"
                                className={s.btn}
                            >
                                Set up Ava in Settings
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
            <AvaConfigHome
                show={showAvaHome}
                onClickBack={() => {
                    setShowAvaHome(false);
                }}
                onClickSet={() => {
                    setShowAvaSettingInProsepect('true');
                    setShowAvaHome(false);
                    navigate(`${PATH.DASHBOARD}/${PATH.AVA_SETTING}`);
                }}
            />
            <div className={`${s.newMsgWrap} ${showNewMsgModal ? s.newMsgWrapShow : ''}`}>
                <div className={s.nTitle}>
                    <span>New message</span>
                    <svg
                        onClick={() => {
                            setNewMsgClientId(undefined);
                            setNewMsgContent('');
                            setNewMsgIdentityId(undefined);
                            setResetFlag({});
                            setShowNewMsgModal(false);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#4B5563"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                <div className={s.nContent}>
                    <div className={s.nLabel}>Client</div>
                    <Select
                        showSearch
                        className={s.select}
                        onChange={(val) => {
                            setNewMsgClientId(val);
                        }}
                        filterOption={(input, option) => {
                            const name = option?.children?.join('');
                            if (!name) return true;
                            return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                        }}
                        value={newMsgClientId}
                        placeholder="Select a client"
                        style={{ width: '200px' }}
                    >
                        {listData?.map((item: Prospect) => {
                            const val = item.id;
                            return (
                                <Select.Option value={val} key={val}>
                                    {item.firstName} {item.lastName}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    <div className={s.nLabel}>Message</div>
                    <div className={s.inputWrap}>
                        <AiTextEditor
                            size="small"
                            rows={6}
                            maxLength={3000}
                            onChange={(e) => {
                                if (typeof e === 'string') {
                                    setNewMsgContent(e as string);
                                } else {
                                    setNewMsgContent(e ? e.target.value : '');
                                }
                            }}
                            value={newMsgContent}
                            type={EAIEditorType.PROSPECT}
                            resetFlag={resetFlag}
                            placeholder=""
                        />
                    </div>
                </div>
                <div className={s.nFooter}>
                    <Button style={{ marginRight: '16px' }} onClick={onClickDiscard}>
                        Discard
                    </Button>
                    <Button
                        loading={sendingNewMsg}
                        disabled={!newMsgClientId || !newMsgContent}
                        type="primary"
                        onClick={onClickSend}
                    >
                        Send
                    </Button>
                </div>
            </div>
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_CLIENTS} defaultShow={false} makeFix={false} />
            </div>
        </div>
    );
};

export default Page;
